import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  ArrowLeft,
  Save,
  Clock,
  AlertTriangle,
  MessageSquare,
  Truck,
  BellRing,
  BellOff,
  CheckCircle,
  XCircle,
  Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useNotifications } from '../hooks/useNotifications';

export const NotificationsPage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const { permission, supported, requestPermission, sendNotification } = useNotifications();
  const [requestingPermission, setRequestingPermission] = useState(false);
  
  const [settings, setSettings] = useState({
    hoursLimit: true,
    cycleLimit: true,
    inspectionReminder: true,
    messages: true
  });

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    setSaving(true);
    localStorage.setItem('notificationSettings', JSON.stringify(settings));
    setTimeout(() => {
      setSaving(false);
      toast.success('Préférences enregistrées');
      navigate('/profile');
    }, 500);
  };

  const handleRequestPermission = async () => {
    setRequestingPermission(true);
    const result = await requestPermission();
    setRequestingPermission(false);
    
    if (result.granted) {
      toast.success('Notifications activées!');
      // Envoyer une notification de test
      setTimeout(() => {
        sendNotification(
          '✅ Notifications activées',
          'Vous recevrez maintenant des alertes importantes.',
          { type: 'general' }
        );
      }, 1000);
    } else if (result.permission === 'denied') {
      toast.error('Notifications refusées. Activez-les dans les paramètres de votre navigateur.');
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem('notificationSettings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }
  }, []);

  const notificationOptions = [
    {
      id: 'hoursLimit',
      icon: Clock,
      title: 'Limite d\'heures',
      description: 'Alerte à 90% de la limite journalière (13h conduite / 14h service)',
      color: 'text-amber-500'
    },
    {
      id: 'cycleLimit',
      icon: AlertTriangle,
      title: 'Limite de cycle',
      description: 'Alerte quand il reste moins de 10h sur le cycle',
      color: 'text-red-500'
    },
    {
      id: 'inspectionReminder',
      icon: Truck,
      title: 'Rappel d\'inspection',
      description: 'Rappel quotidien pour l\'inspection pré-départ',
      color: 'text-green-500'
    },
    {
      id: 'messages',
      icon: MessageSquare,
      title: 'Nouveaux messages',
      description: 'Notification pour les messages du dispatch',
      color: 'text-blue-500'
    }
  ];

  const getPermissionStatus = () => {
    if (!supported) {
      return { icon: BellOff, text: 'Non supporté', color: 'text-slate-500', bg: 'bg-slate-800' };
    }
    switch (permission) {
      case 'granted':
        return { icon: CheckCircle, text: 'Activées', color: 'text-green-500', bg: 'bg-green-900/30' };
      case 'denied':
        return { icon: XCircle, text: 'Bloquées', color: 'text-red-500', bg: 'bg-red-900/30' };
      default:
        return { icon: BellRing, text: 'Non configurées', color: 'text-amber-500', bg: 'bg-amber-900/30' };
    }
  };

  const permissionStatus = getPermissionStatus();
  const PermissionIcon = permissionStatus.icon;

  return (
    <div className="page-wrapper" data-testid="notifications-page">
      {/* Header */}
      <div className="app-header">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/profile')}
            className="p-2 hover:bg-slate-800 rounded-lg"
            data-testid="back-button"
          >
            <ArrowLeft className="text-slate-400" size={24} />
          </button>
          <div className="flex items-center gap-3">
            <Bell className="text-amber-500" size={28} />
            <div>
              <h1 className="text-2xl text-white">Notifications</h1>
              <p className="text-slate-400 text-sm">Gérer les alertes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Permission Status Card */}
        <div className={`${permissionStatus.bg} border border-slate-800 rounded-xl p-4`} data-testid="permission-status">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center`}>
              <PermissionIcon className={permissionStatus.color} size={24} />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold">Notifications Push</p>
              <p className={`text-sm ${permissionStatus.color}`}>{permissionStatus.text}</p>
            </div>
            
            {supported && permission === 'default' && (
              <Button
                onClick={handleRequestPermission}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                disabled={requestingPermission}
                data-testid="enable-notifications"
              >
                {requestingPermission ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    ...
                  </span>
                ) : (
                  'Activer'
                )}
              </Button>
            )}
            
            {permission === 'denied' && (
              <div className="text-xs text-slate-400 max-w-[120px] text-right">
                Activez dans les paramètres du navigateur
              </div>
            )}
          </div>
          
          {!supported && (
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-400">
              <Smartphone size={16} />
              <span>Votre navigateur ne supporte pas les notifications push</span>
            </div>
          )}
          
          {supported && permission === 'granted' && (
            <p className="mt-3 text-sm text-green-400">
              Vous recevrez des alertes même quand l'app est en arrière-plan.
            </p>
          )}
        </div>

        {/* Notification Types */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-800">
            <p className="text-white font-bold">Types d'alertes</p>
            <p className="text-slate-500 text-sm">Choisissez les notifications que vous souhaitez recevoir</p>
          </div>
          
          {notificationOptions.map((option, index) => {
            const Icon = option.icon;
            return (
              <div 
                key={option.id}
                className={`p-4 flex items-center gap-4 ${index !== 0 ? 'border-t border-slate-800' : ''}`}
              >
                <div className={`w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center ${option.color}`}>
                  <Icon size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{option.title}</p>
                  <p className="text-slate-500 text-sm">{option.description}</p>
                </div>
                <button
                  onClick={() => handleToggle(option.id)}
                  className={`w-14 h-8 rounded-full transition-colors relative ${
                    settings[option.id] ? 'bg-green-500' : 'bg-slate-700'
                  }`}
                  data-testid={`toggle-${option.id}`}
                >
                  <div 
                    className={`w-6 h-6 rounded-full bg-white absolute top-1 transition-transform ${
                      settings[option.id] ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="bg-slate-800/50 rounded-xl p-4">
          <p className="text-slate-400 text-sm">
            <strong className="text-white">Comment ça marche:</strong>
            <br />• Les alertes d'heures se déclenchent automatiquement à 90% de vos limites
            <br />• Le rappel d'inspection se déclenche le matin si aucune inspection n'a été faite
            <br />• Les notifications de messages arrivent en temps réel
          </p>
        </div>

        <Button
          onClick={handleSave}
          className="w-full h-14 btn-primary-glow"
          disabled={saving}
          data-testid="save-notifications"
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Enregistrement...
            </span>
          ) : (
            <>
              <Save size={20} className="mr-2" />
              Enregistrer les préférences
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default NotificationsPage;
